import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Send, ChevronRight, ChevronLeft, FileText, CheckCircle } from 'lucide-react'
import { listFacilityContacts, sendReport, type FacilityContact } from '../../lib/services/facilityContacts'

interface SendReportModalProps {
  reportId: string
  facilityId: string
  onClose: () => void
  onSent?: (deliveryIds: string[]) => void
}

type Step = 'recipients' | 'compose' | 'confirm'

const MAX_MESSAGE_LENGTH = 2000

export function SendReportModal({ reportId, facilityId, onClose, onSent }: SendReportModalProps) {
  const qc = useQueryClient()
  const [step, setStep] = useState<Step>('recipients')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [ccInput, setCcInput] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['facility-contacts', facilityId],
    queryFn: () => listFacilityContacts(facilityId),
    enabled: !!facilityId,
  })

  const sendMutation = useMutation({
    mutationFn: async () => {
      const ccEmails = ccInput
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.includes('@'))
      return sendReport({
        report_id: reportId,
        contact_ids: [...selectedIds],
        cc_emails: ccEmails,
        message,
      })
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['report-deliveries', reportId] })
      onSent?.(result.delivery_ids)
    },
    onError: (err: Error) => setError(err.message),
  })

  function toggleContact(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectedContacts = contacts.filter((c) => selectedIds.has(c.contact_id))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-base font-bold text-slate-800">Send Report PDF</h2>
            <p className="text-xs text-slate-500">
              Step {step === 'recipients' ? 1 : step === 'compose' ? 2 : 3} of 3
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Step 1: Recipients */}
          {step === 'recipients' && (
            <div>
              <h3 className="mb-3 text-sm font-bold text-slate-700">Select recipients</h3>
              {isLoading ? (
                <p className="text-sm text-slate-500">Loading contacts…</p>
              ) : contacts.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No contacts on this facility.{' '}
                  <a href={`/facilities/${facilityId}/contacts`} className="text-blue-600 underline">
                    Add a contact
                  </a>{' '}
                  first.
                </p>
              ) : (
                <ul className="space-y-2 max-h-56 overflow-y-auto">
                  {contacts.map((c: FacilityContact) => (
                    <li key={c.contact_id}>
                      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.contact_id)}
                          onChange={() => toggleContact(c.contact_id)}
                          className="mt-0.5 rounded"
                        />
                        <div>
                          <div className="text-sm font-semibold text-slate-800">
                            {c.contact_name}
                            {c.is_primary && (
                              <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-bold text-amber-700">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{c.email}</div>
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-4">
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  CC (optional, comma-separated)
                </label>
                <input
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="name[at]facility.test"
                />
              </div>
            </div>
          )}

          {/* Step 2: Compose */}
          {step === 'compose' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <p className="flex items-center gap-2 font-semibold">
                  <FileText size={14} /> Final report PDF
                </p>
                <p className="mt-1 text-xs leading-relaxed">
                  The released report PDF generated by the report workflow will be attached automatically.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                  rows={4}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Add a brief note to the recipient…"
                />
                <p className="mt-0.5 text-right text-xs text-slate-400">
                  {message.length}/{MAX_MESSAGE_LENGTH}
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && (
            <div>
              <h3 className="mb-3 text-sm font-bold text-slate-700">Review and send</h3>
              <div className="space-y-3 rounded-xl bg-slate-50 p-4 text-sm">
                <div>
                  <span className="font-semibold text-slate-600">To:</span>
                  <ul className="mt-1 list-none space-y-0.5">
                    {selectedContacts.map((c) => (
                      <li key={c.contact_id} className="text-slate-800">
                        {c.contact_name} &lt;{c.email}&gt;
                      </li>
                    ))}
                  </ul>
                </div>
                {ccInput && (
                  <div>
                    <span className="font-semibold text-slate-600">CC:</span>{' '}
                    <span className="text-slate-700">{ccInput}</span>
                  </div>
                )}
                <div>
                  <span className="font-semibold text-slate-600">Attachment:</span>{' '}
                  <span className="text-slate-700">Generated final report PDF</span>
                </div>
                {message && (
                  <div>
                    <span className="font-semibold text-slate-600">Note:</span>
                    <p className="mt-0.5 text-slate-700">{message}</p>
                  </div>
                )}
              </div>
              {sendMutation.isSuccess && (
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700">
                  <CheckCircle size={16} /> Report sent successfully.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
          <button
            onClick={() => {
              if (step === 'recipients') onClose()
              else if (step === 'compose') setStep('recipients')
              else setStep('compose')
            }}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            <ChevronLeft size={16} />
            {step === 'recipients' ? 'Cancel' : 'Back'}
          </button>

          {step !== 'confirm' ? (
            <button
              onClick={() => {
                if (step === 'recipients') {
                  if (selectedIds.size === 0) { setError('Select at least one recipient'); return }
                  setError(null)
                  setStep('compose')
                } else {
                  setError(null)
                  setStep('confirm')
                }
              }}
              className="flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Next <ChevronRight size={16} />
            </button>
          ) : sendMutation.isSuccess ? (
            <button
              onClick={onClose}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
            >
              Done
            </button>
          ) : (
            <button
              onClick={() => sendMutation.mutate()}
              disabled={sendMutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Send size={16} />
              {sendMutation.isPending ? 'Sending…' : 'Send Report'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
